import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { dashboardPanelSx } from '@/shared/styles/surfaces';
import type { ActivityAction, ProgressEntry, RewardActivityView } from '../types';
import { RewardChip } from './RewardChip';

const GOLD = '#C8A96E';

function iconFor(activity: RewardActivityView): ReactNode {
  if (activity.trigger === 'REFERRAL') return <GroupAddRoundedIcon />;
  if (activity.trigger === 'TSHIRT_SELFIE') return <CheckroomRoundedIcon />;
  return activity.progress.travellerPhotos ? <PhotoCameraRoundedIcon /> : <EventAvailableRoundedIcon />;
}

function ProgressRow({ label, entry }: { label: string; entry: ProgressEntry }) {
  const value = entry.target ? Math.min(100, (entry.current / entry.target) * 100) : 0;
  const done = entry.current >= entry.target;
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={800} color={done ? 'success.main' : 'text.primary'}>
          {entry.current} / {entry.target}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        aria-label={`${label}: ${entry.current} / ${entry.target}`}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'grey.100',
          '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: done ? 'success.main' : GOLD },
        }}
      />
    </Box>
  );
}

interface EarnRewardsSectionProps {
  activities?: RewardActivityView[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onAction: (action: ActivityAction) => void;
}

export function EarnRewardsSection({ activities, loading, error, onRetry, onAction }: EarnRewardsSectionProps) {
  const { t } = useTranslation('rewards');

  return (
    <Box id="earn" sx={{ scrollMarginTop: 88 }}>
      <DashboardSectionHeader title={t('earn.title')} subtitle={t('earn.subtitle')} />
      {error ? (
        <Alert severity="error" action={<Button onClick={onRetry}>{t('page.retry')}</Button>}>
          {t('common.error')}
        </Alert>
      ) : loading ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={220} />
          ))}
        </Box>
      ) : !activities?.length ? (
        <Paper elevation={0} sx={{ ...dashboardPanelSx, p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t('earn.empty')}</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
          {activities.map((activity) => {
            const credited = activity.state === 'CREDITED';
            return (
              <Paper
                key={activity.id}
                elevation={0}
                sx={{
                  ...dashboardPanelSx,
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  borderColor: credited ? 'rgba(200,169,110,0.55)' : 'divider',
                  background: credited ? 'linear-gradient(180deg, rgba(200,169,110,0.08), transparent 60%)' : undefined,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      borderRadius: '14px',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#1A2E1A',
                      bgcolor: 'rgba(200,169,110,0.2)',
                    }}
                  >
                    {credited ? <VerifiedRoundedIcon /> : iconFor(activity)}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                      <Typography fontWeight={800} sx={{ lineHeight: 1.3 }}>
                        {activity.title}
                      </Typography>
                      <RewardChip label={t(`status.activity.${activity.state}`)} status={activity.state} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
                      {activity.description}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.03em', color: '#7A5F27' }}>
                    +{activity.credits.toLocaleString('en-IN')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    {t('common.credits')}
                    {activity.repeatability === 'PER_REFERRAL' && ` · ${t('earn.perGuide')}`}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gap: 1.25 }}>
                  {activity.progress.bookings && (
                    <ProgressRow label={t('earn.progress.bookings')} entry={activity.progress.bookings} />
                  )}
                  {activity.progress.travellerPhotos && (
                    <ProgressRow label={t('earn.progress.travellerPhotos')} entry={activity.progress.travellerPhotos} />
                  )}
                  {activity.progress.tshirt && (
                    <ProgressRow label={t('earn.progress.tshirt')} entry={activity.progress.tshirt} />
                  )}
                  {activity.progress.approvedReferrals && (
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {t('earn.progress.approvedReferrals', { count: activity.progress.approvedReferrals.current })}
                    </Typography>
                  )}
                  {activity.statusNote && activity.action === 'TRACK_TSHIRT' && (
                    <Typography variant="caption" color="text.secondary">
                      {t('earn.merchStatus', {
                        status: t(`status.merch.${activity.statusNote as 'REQUESTED'}`, { defaultValue: activity.statusNote }),
                      })}
                    </Typography>
                  )}
                  {activity.statusNote && activity.action === 'UPLOAD_SELFIE' && (
                    <Typography variant="caption" color="error.main">
                      {t('earn.rejectedNote', { reason: activity.statusNote })}
                    </Typography>
                  )}
                  {(activity.creditsEarned > 0 || activity.creditsPending > 0) && (
                    <Typography variant="caption" fontWeight={700} color={activity.creditsPending ? 'warning.dark' : 'success.dark'}>
                      {activity.creditsEarned > 0 && t('earn.earned', { credits: activity.creditsEarned })}
                      {activity.creditsEarned > 0 && activity.creditsPending > 0 && ' · '}
                      {activity.creditsPending > 0 && t('earn.pending', { credits: activity.creditsPending })}
                    </Typography>
                  )}
                </Box>

                {activity.action !== 'NONE' && (
                  <Button
                    variant={activity.action === 'SHARE' ? 'contained' : 'outlined'}
                    onClick={() => onAction(activity.action)}
                    sx={{
                      mt: 'auto',
                      alignSelf: 'flex-start',
                      borderRadius: 999,
                      fontWeight: 700,
                      ...(activity.action === 'SHARE'
                        ? { bgcolor: '#1A2E1A', '&:hover': { bgcolor: '#243D24' } }
                        : { color: '#1A2E1A', borderColor: 'rgba(26,46,26,0.35)' }),
                    }}
                  >
                    {t(`earn.actions.${activity.action}`)}
                  </Button>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
